import React, { useEffect } from "react";
import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CardHeader,
  Typography,
  Tabs,
  Tab,
  Grid,
  Button,
} from "@mui/material";
import { getTimeStamp } from "../../utils/common";
import { spacing } from "@mui/system";
import TabPanel from "../../components/TabPanel";
import { a11yProps } from "../../utils/common";
import BasicInformation from "./components/BasicInformation";
import AgreementReceived from "./components/AgreementReceived";
import { useDialog } from "../../contexts/DialogContext";
import useAgreementTracker from "../../hooks/useAgreementTracker";
import { useNavigate, useSearchParams } from "react-router-dom";
import AgreementDispatched from "./components/AgreementDispatched";
import CourierVerify from "./components/CourierVerified";
import IspAgreementReceived from "./components/IspAgreementReceived";
import IDSAgreement from "./components/IspDispatchSignedAgreement";
import { green, red, orange, pink, purple, blue, grey } from "@mui/material/colors";
import SignedAgreementReceived from "./components/SignedAgreementReceived";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);

// Extracted to avoid re-creation
const AgreementTrackerDetailsBreadcrumbs = () => (
  <Breadcrumbs aria-label="Breadcrumb" mt={2}>
    <Typography>Agreement Tracker</Typography>
    <Typography>Agreement Tracker Details</Typography>
  </Breadcrumbs>
);

const validationSchema = Yup.object().shape({
  tenAgrMntDispatchedDate: Yup.string().required("Required"),
  agreementReceivedDate: Yup.string().required("Required"),
  tenDocketNumber: Yup.string().required("Required"),
  tenCourierType: Yup.string().required("Required"),
  tenCourierName: Yup.string().required("Required"),
  comments: Yup.string().required("Required"),
});

function AgTrackDetails() {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = React.useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  let [searchParams] = useSearchParams();

  const id = searchParams.get("id");

  const { getAllAgreementTrackerHistory, getAgreementTracker, agreementTracker } =
    useAgreementTracker();

  const { openDialog } = useDialog();

  // Derived payload instead of state
  const payload = React.useMemo(
    () => ({
      filters: [
        {
          fieldName: "agreementTrackerHistoryId",
          fieldValue: id,
          op: "eq",
          dataType: "int",
          condition: "and",
          isJoin: true,
        },
      ],
      pagination: {
        pageNumber: 1,
        pageSize: 100,
      },
      sortFields: [
        {
          field: "createdAt",
          sort: "DESC",
        },
      ],
    }),
    [id],
  );

  useEffect(() => {
    if (id) {
      getAgreementTracker(id);
    }
  }, [id, getAgreementTracker]);

  useEffect(() => {
    getAllAgreementTrackerHistory(payload);
  }, [payload, getAllAgreementTrackerHistory]);

  const fetchData = async () => {
    await getAgreementTracker(id);
  };

  const tenantAgreementReceived = () => {
    return openDialog(
      "agreement_received",
      "Agreement Received",
      <AgreementReceived
        key={getTimeStamp()}
        fetchData={fetchData}
        fields={validationSchema.fields}
        agreementTrackerId={id}
      />,
    );
  };

  const tenantAgreementDispatched = () => {
    return openDialog(
      "agreement_dispatched",
      "Agreement Dispatched",
      <AgreementDispatched
        key={getTimeStamp()}
        fetchData={fetchData}
        fields={validationSchema.fields}
        agreementTrackerId={id}
      />,
    );
  };

  const tenantCourierVerified = () => {
    return openDialog(
      "courier_verified",
      "Courier Verified",
      <CourierVerify
        key={getTimeStamp()}
        fetchData={fetchData}
        fields={validationSchema.fields}
        agreementTrackerId={id}
      />,
    );
  };

  const ispAgreementReceived = () => {
    return openDialog(
      "isp_agreement_received",
      "ISP Agreement Received",
      <IspAgreementReceived
        key={getTimeStamp()}
        fetchData={fetchData}
        fields={validationSchema.fields}
        agreementTrackerId={id}
      />,
    );
  };

  const idsAgreement = () => {
    return openDialog(
      "isp_dispatch_signed_agreement",
      "ISP Dispatch Signed Agreement",
      <IDSAgreement
        key={getTimeStamp()}
        fetchData={fetchData}
        fields={validationSchema.fields}
        agreementTrackerId={id}
      />,
    );
  };

  const signedAgreementReceived = () => {
    return openDialog(
      "signed_agreement_received",
      "Signed Agreement Received",
      true,
      <SignedAgreementReceived
        key={getTimeStamp()}
        fetchData={fetchData}
        fields={validationSchema.fields}
        agreementTrackerId={id}
      />,
    );
  };

  return (
    <React.Fragment>
      <Card mb={6}>
        <CardHeader
          title={t("ag_tracker_details")}
          subheader={<AgreementTrackerDetailsBreadcrumbs />}
        />
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            {agreementTracker && agreementTracker.status == "ISP_AGRMT_DISPATCHED" && (
              <Button variant="contained" onClick={tenantAgreementReceived}>
                Tenant Agreement Received
              </Button>
            )}
            {agreementTracker && agreementTracker.status == "TENANT_AGRMT_RECEIVED" && (
              <Button variant="contained" color="warning" onClick={tenantAgreementDispatched}>
                Tenant Agreement Dispatched
              </Button>
            )}
            {agreementTracker && agreementTracker.status == "TENANT_AGRMT_DISPATCHED" && (
              <Button variant="contained" color="success" onClick={tenantCourierVerified}>
                Tenant Courier Verified
              </Button>
            )}
            {agreementTracker && agreementTracker.status == "COURIER_VERIFIED" && (
              <Button variant="contained" color="error" onClick={ispAgreementReceived}>
                ISP Agreement Received
              </Button>
            )}
            {agreementTracker && agreementTracker.status == "ISP_AGRMT_RECEIVED" && (
              <Button variant="contained" color="secondary" onClick={idsAgreement}>
                ISP Dispatch Signed Agreement
              </Button>
            )}
            {agreementTracker && agreementTracker.status == "ISP_DISPATCHED_SIGNED_AGRMT" && (
              <Button variant="contained" color="success" onClick={signedAgreementReceived}>
                Tenant Agreement Signed Received
              </Button>
            )}
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="Branch">
              <Tab label="Basic Information" {...a11yProps(0)} />
            </Tabs>
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <BasicInformation />
                </Grid>
              </Grid>
            </TabPanel>
          </Box>
        </CardContent>
      </Card>
    </React.Fragment>
  );
}

export default AgTrackDetails;
